/**
 * timeline.ts
 *
 * Pure helpers that turn the recorded documents produced by Document Processing
 * into a chronologically ordered timeline for the Title Search (TR)
 * `Job_Detail_Page`.
 *
 * Each processed document carries `extractedFields` — a JSON array of
 * `{ field_name, field_value, confidence_score, page }` produced by BDA. The
 * "date" used to place a document on the timeline is resolved as follows:
 *
 *   1. The document's recorded/recording date field, when present.
 *   2. For judgments (no recorded date is typically captured), the judgment
 *      date field — falling back to any parseable date field on the judgment.
 *
 * Documents whose date cannot be resolved are kept but sorted to the end so the
 * timeline still surfaces them.
 *
 * This module is intentionally side-effect free and deterministic so it can be
 * unit/property tested in isolation from the React component that renders it.
 */

/** The minimal shape of a processed document the timeline needs. */
export interface TimelineDocument {
  documentId: string;
  documentType?: string | null;
  confidenceScore?: number | null;
  sourceFilename?: string | null;
  pageRange?: string | null;
  /** JSON string: array of { field_name, field_value, confidence_score, page }. */
  extractedFields?: string | null;
}

/** Where a timeline entry's date came from. */
export type TimelineDateSource = 'recorded' | 'judgment' | 'none';

/** The category a recorded document belongs to on the findings page. */
export type TimelineCategory = 'deed' | 'mortgage' | 'judgment' | 'other';

/** A single document positioned on the timeline. */
export interface TimelineEntry {
  documentId: string;
  documentType: string;
  /** The category this document belongs to (deed/mortgage/judgment/other). */
  category: TimelineCategory;
  /** The raw date string used for placement (ISO or otherwise), or null. */
  date: string | null;
  /** Which field the date was taken from. */
  dateSource: TimelineDateSource;
  sourceFilename: string | null;
  pageRange: string | null;
  /** The information extracted from the document, for display. */
  extractedFields: ExtractedField[];
}

/** A single extracted field surfaced for display. */
export interface ExtractedField {
  /** The field name formatted for display (Title Case with spaces). */
  label: string;
  /** The display value, joined when the underlying value is a list. */
  value: string;
  /** The individual values when the field holds a list; empty otherwise. */
  values: string[];
  /** True when the field value is a list and should render as bullets. */
  isList: boolean;
  /** True when the field holds a date and its value(s) should be date-formatted. */
  isDate: boolean;
}

/** A single parsed extracted field used internally for date resolution. */
interface ParsedField {
  /** Normalized (lower-case) field name. */
  name: string;
  /** Original field name as extracted. */
  label: string;
  /** Joined string form of the value (single value, or list joined by ", "). */
  value: string;
  /** The individual values when the field holds a list; single-element otherwise. */
  values: string[];
  /** True when the underlying value was a list. */
  isList: boolean;
}

/** Normalize a field name to a lower-case, separator-insensitive form. */
function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Format a raw field name as Title Case with spaces for display, e.g.
 * "recording_date" -> "Recording Date", "grantorName" -> "Grantor Name".
 * Handles snake_case, kebab-case, camelCase, PascalCase, and spaced input.
 */
export function toDisplayLabel(name: string): string {
  if (!name) return '';
  const spaced = name
    .replace(/[_-]+/g, ' ')
    // split camelCase / PascalCase boundaries
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return spaced
    .split(' ')
    .map((word) =>
      word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word
    )
    .join(' ');
}

/**
 * Coerce a raw extracted `field_value` into a list of string values.
 * Handles native arrays as well as JSON-stringified arrays (e.g. '["a","b"]').
 * Returns `{ values, isList }` where `isList` is true only when the value was
 * an actual list with more than one entry's worth of structure.
 */
function coerceFieldValues(raw: unknown): { values: string[]; isList: boolean } {
  let value: unknown = raw;

  // A string that is itself a JSON array (common from some extractors).
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const reparsed = JSON.parse(trimmed);
        if (Array.isArray(reparsed)) {
          value = reparsed;
        }
      } catch {
        // Not valid JSON. A Python-style list such as "['A', 'B']" is the
        // other common shape; anything else is treated as a plain string.
        const pyList = /^\[\s*'([\s\S]*)'\s*\]$/.exec(trimmed);
        if (pyList) value = pyList[1].split(/'\s*,\s*'/);
      }
    }
  }

  if (Array.isArray(value)) {
    const values = value
      .map((v) => String(v ?? '').trim())
      .filter((v) => v !== '');
    return { values, isList: true };
  }

  const single = String(value ?? '').trim();
  return { values: single === '' ? [] : [single], isList: false };
}

/**
 * True for a stored field the reviewer rejected (`action: 'REJECT'`). A
 * finalized review keeps these so it can show them again, but they are not
 * part of the document's information.
 */
export function isRejectedEntry(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const action = (raw as Record<string, unknown>).action;
  return typeof action === 'string' && action.toUpperCase() === 'REJECT';
}

/** Parse a document's `extractedFields` JSON into a tolerant list of fields. */
export function parseExtractedFields(extractedFields?: string | null): ParsedField[] {
  if (!extractedFields) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(extractedFields);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((raw) => !isRejectedEntry(raw)).map((raw) => {
    const f = (raw ?? {}) as Record<string, unknown>;
    const label = String(
      (f.field_name as string) ?? (f.field as string) ?? ''
    ).trim();
    const { values, isList } = coerceFieldValues(
      f.field_value !== undefined ? f.field_value : f.value
    );
    return {
      name: normalizeName(label),
      label,
      value: values.join(', '),
      values,
      isList,
    };
  });
}

/**
 * Parse a date string into a sortable UTC timestamp, or null when it is
 * missing/unparseable. ISO `YYYY-MM-DD` is read directly to avoid local
 * timezone shifts; anything else falls back to `Date.parse`.
 */
export function parseTimestamp(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (raw === '') return null;
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const ts = Date.UTC(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(ts) ? null : ts;
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

/** True when a document type looks like a judgment (tolerant of spelling). */
export function isJudgmentType(documentType: string | null | undefined): boolean {
  const t = (documentType ?? '').toLowerCase();
  return t.includes('judgment') || t.includes('judgement');
}

/**
 * True when a document type is a mortgage-related instrument: mortgages,
 * assignments (of mortgage), and deeds of trust.
 */
export function isMortgageType(documentType: string | null | undefined): boolean {
  const t = (documentType ?? '').toLowerCase();
  return (
    t.includes('mortgage') ||
    t.includes('assignment') ||
    t.includes('deed of trust')
  );
}

/**
 * True when a document type is a recorded deed (warranty, quit claim, grant,
 * etc.). Deeds of trust are excluded here as they are mortgage instruments.
 */
export function isDeedType(documentType: string | null | undefined): boolean {
  const t = (documentType ?? '').toLowerCase();
  return t.includes('deed') && !t.includes('deed of trust');
}

/**
 * Classify a recorded document into a findings category. Order matters:
 * judgments and mortgage instruments are matched before generic deeds so that
 * e.g. "deed of trust" lands in the mortgage section.
 */
export function classifyDocument(
  documentType: string | null | undefined
): TimelineCategory {
  if (isJudgmentType(documentType)) return 'judgment';
  if (isMortgageType(documentType)) return 'mortgage';
  if (isDeedType(documentType)) return 'deed';
  return 'other';
}

/**
 * Find the first field matching `predicate` whose value parses to a valid date.
 * Falls back to the first matching field with a non-empty value when none of
 * the matches are parseable, so a present-but-odd date is still surfaced.
 */
function findDateField(
  fields: ParsedField[],
  predicate: (name: string) => boolean
): ParsedField | null {
  let firstNonEmpty: ParsedField | null = null;
  for (const f of fields) {
    if (!predicate(f.name)) continue;
    if (firstNonEmpty === null && f.value !== '') {
      firstNonEmpty = f;
    }
    if (parseTimestamp(f.value) !== null) {
      return f;
    }
  }
  return firstNonEmpty;
}

/**
 * Resolve the date used to place a document on the timeline along with its
 * source: the recorded/recording date when present, otherwise (for judgments)
 * the judgment date, otherwise any date field on the judgment.
 */
export function resolveTimelineDate(
  documentType: string | null | undefined,
  fields: ParsedField[]
): { date: string | null; source: TimelineDateSource } {
  const recorded = findDateField(
    fields,
    (n) => n.includes('record') && n.includes('date')
  );
  if (recorded && recorded.value !== '') {
    return { date: recorded.value, source: 'recorded' };
  }

  if (isJudgmentType(documentType)) {
    const judgment =
      findDateField(
        fields,
        (n) =>
          (n.includes('judgment') || n.includes('judgement')) &&
          n.includes('date')
      ) ?? findDateField(fields, (n) => n.includes('date'));
    if (judgment && judgment.value !== '') {
      return { date: judgment.value, source: 'judgment' };
    }
  }

  return { date: null, source: 'none' };
}

/** Build a single timeline entry from a document. */
export function toTimelineEntry(doc: TimelineDocument): TimelineEntry {
  const fields = parseExtractedFields(doc.extractedFields);
  const { date, source } = resolveTimelineDate(doc.documentType, fields);
  return {
    documentId: doc.documentId,
    documentType: doc.documentType ?? 'Document',
    category: classifyDocument(doc.documentType),
    date,
    dateSource: source,
    sourceFilename: doc.sourceFilename ?? null,
    pageRange: doc.pageRange ?? null,
    extractedFields: fields
      .filter((f) => f.label !== '' || f.value !== '')
      .map((f) => ({
        label: toDisplayLabel(f.label),
        value: f.value,
        values: f.values,
        isList: f.isList,
        isDate: f.name.includes('date'),
      })),
  };
}

/**
 * Build the timeline: every document as an entry, ordered by resolved date from
 * oldest to newest. Undated documents keep their relative order and sort to the
 * end (stable). The input is not mutated.
 */
export function buildTimeline(documents: TimelineDocument[]): TimelineEntry[] {
  const entries = documents.map((doc, index) => ({
    entry: toTimelineEntry(doc),
    index,
  }));
  entries.sort((a, b) => {
    const ta = parseTimestamp(a.entry.date);
    const tb = parseTimestamp(b.entry.date);
    if (ta === tb) return a.index - b.index;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return ta - tb;
  });
  return entries.map((e) => e.entry);
}

/** A timeline split into the findings categories, each chronologically sorted. */
export interface GroupedTimeline {
  deeds: TimelineEntry[];
  mortgages: TimelineEntry[];
  judgments: TimelineEntry[];
  other: TimelineEntry[];
}

/**
 * Build the timeline and group entries by category. Each group preserves the
 * oldest-to-newest ordering produced by `buildTimeline`.
 */
export function buildGroupedTimeline(
  documents: TimelineDocument[]
): GroupedTimeline {
  const timeline = buildTimeline(documents);
  return {
    deeds: timeline.filter((e) => e.category === 'deed'),
    mortgages: timeline.filter((e) => e.category === 'mortgage'),
    judgments: timeline.filter((e) => e.category === 'judgment'),
    other: timeline.filter((e) => e.category === 'other'),
  };
}
